import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bookmark, Lock, Globe, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CollectionDetail() {
    const { user } = useAuth();
    const { cid } = useParams();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/collections/${cid}`)
            .then((r) => setC(r.data))
            .finally(() => setLoading(false));
    }, [cid]);

    async function removeItem(target_type, target_id) {
        try {
            await api.delete(`/collections/${cid}/items`, { params: { target_type, target_id } });
            setC((cur) => cur ? { ...cur, items: (cur.items || []).filter((i) => !(i.target_type === target_type && i.target_id === target_id)) } : cur);
        } catch (e) { toast.error("Remove failed"); }
    }

    if (loading) return <div className="ok-container py-12 font-mono text-xs opacity-50">Loading collection…</div>;
    if (!c) return <div className="ok-container py-12">Collection not available.</div>;

    const isOwn = user && user.id === c.user_id;
    const items = c.items || [];

    return (
        <div className="ok-container py-10 max-w-4xl" data-testid="collection-detail-page">
            <Link to="/collections" className="font-mono text-[10px] uppercase tracking-widest opacity-60 inline-flex items-center gap-1 hover:text-[var(--ok-accent)]">
                <ArrowLeft className="w-3 h-3" /> All collections
            </Link>
            <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl flex items-center gap-2">
                        <Bookmark className="w-6 h-6 text-[var(--ok-accent)]" />
                        {c.name}
                    </h1>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mt-1 flex items-center gap-2">
                        Curated by <Link to={`/u/${c.user_id}`} className="hover:text-[var(--ok-accent)]">{c.user_name}</Link>
                        {c.is_public ? <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Public</span> : <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Private</span>}
                    </div>
                </div>
            </div>
            {c.description && <p className="mt-3 text-sm opacity-80 max-w-2xl whitespace-pre-wrap">{c.description}</p>}

            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-8 mb-3">Items ({items.length})</div>

            {items.length === 0 ? (
                <div className="ok-card p-6 text-sm opacity-70 text-center" data-testid="collection-empty">
                    {isOwn ? <>No items yet. Open an encounter and use the <strong>Add to collection</strong> button to bookmark it here.</> : "Nothing here yet."}
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((it) => (
                        <div key={`${it.target_type}-${it.target_id}`} className="ok-card p-4 flex gap-3" data-testid={`collection-item-${it.target_id}`}>
                            {it.thumbnail_url && (
                                <img src={it.thumbnail_url.startsWith("http") ? it.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${it.thumbnail_url}`}
                                     alt="" className="w-20 h-20 object-cover rounded-sm flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">{it.target_type}</div>
                                {it.target_type === "encounter" ? (
                                    <Link to={`/encounters/${it.target_id}`} className="font-display text-lg hover:text-[var(--ok-accent)]">{it.title || it.target_id}</Link>
                                ) : (
                                    <div className="font-display text-lg">{it.title || it.target_id}</div>
                                )}
                                {it.note && <p className="text-xs opacity-70 mt-1 whitespace-pre-wrap">{it.note}</p>}
                                <div className="font-mono text-[10px] opacity-50 mt-1">Added {new Date(it.added_at).toLocaleDateString()}</div>
                            </div>
                            {isOwn && (
                                <button
                                    onClick={() => removeItem(it.target_type, it.target_id)}
                                    className="ok-btn-outline text-xs h-fit inline-flex items-center gap-1"
                                    data-testid={`collection-item-remove-${it.target_id}`}
                                >
                                    <Trash2 className="w-3 h-3" /> Remove
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
